"use client"

import { motion } from "framer-motion"
import { Icon } from "@/shared/components/icons"

const HEARTS = [
  { top: "9%", left: "12%", size: 18, delay: 0 },
  { top: "17%", left: "78%", size: 13, delay: 0.7 },
  { top: "31%", left: "22%", size: 14, delay: 1.1 },
  { top: "42%", left: "88%", size: 20, delay: 0.3 },
  { top: "58%", left: "10%", size: 15, delay: 1.5 },
  { top: "68%", left: "72%", size: 12, delay: 0.9 },
  { top: "82%", left: "42%", size: 17, delay: 1.8 },
] as const

const SPARKS = [
  { top: "14%", left: "38%", delay: 0.2 },
  { top: "25%", left: "64%", delay: 1.0 },
  { top: "36%", left: "44%", delay: 0.5 },
  { top: "52%", left: "80%", delay: 1.4 },
  { top: "76%", left: "18%", delay: 0.8 },
  { top: "88%", left: "68%", delay: 1.7 },
] as const

/** Signup-gate backdrop: blue glow and floating hearts — no character silhouette. */
export function SignupGateBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden bg-black" aria-hidden>
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(75% 52% at 50% 42%, color-mix(in srgb, var(--accent-blue) 22%, transparent), transparent 62%), radial-gradient(80% 70% at 50% 100%, color-mix(in srgb, var(--accent-blue) 8%, transparent), transparent 60%), linear-gradient(180deg, color-mix(in srgb, var(--accent-blue) 12%, transparent), rgba(0,0,0,0.92) 74%)",
        }}
      />

      <motion.div
        className="absolute left-1/2 top-[44%] h-52 w-52 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[64px]"
        style={{ background: "color-mix(in srgb, var(--accent-blue) 42%, transparent)" }}
        animate={{ scale: [0.9, 1.15, 0.9], opacity: [0.32, 0.62, 0.32] }}
        transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        className="absolute left-1/2 top-[46%] h-px w-[58%] -translate-x-1/2"
        style={{
          background:
            "linear-gradient(90deg, transparent, color-mix(in srgb, var(--accent-blue) 36%, white), transparent)",
          boxShadow: "0 0 18px color-mix(in srgb, var(--accent-blue) 28%, transparent)",
        }}
        animate={{ opacity: [0.08, 0.36, 0.08], scaleX: [0.76, 1, 0.76] }}
        transition={{ duration: 4.4, repeat: Infinity, ease: "easeInOut" }}
      />

      {HEARTS.map((heart, i) => (
        <motion.div
          key={i}
          className="absolute text-[var(--accent-blue)] drop-shadow-[0_0_14px_color-mix(in_srgb,var(--accent-blue)_55%,transparent)]"
          style={{ top: heart.top, left: heart.left }}
          animate={{ y: [0, -12, 0], opacity: [0.2, 0.68, 0.2], scale: [0.92, 1.1, 0.92] }}
          transition={{ duration: 4.4 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: heart.delay }}
        >
          <Icon name="heart" variant="bold" size={heart.size} />
        </motion.div>
      ))}

      {SPARKS.map((spark, i) => (
        <motion.span
          key={i}
          className="absolute size-1 rounded-full bg-white/80"
          style={{ top: spark.top, left: spark.left }}
          animate={{ opacity: [0.2, 0.9, 0.2], scale: [0.75, 1.4, 0.75] }}
          transition={{ duration: 2.7 + (i % 2), repeat: Infinity, ease: "easeInOut", delay: spark.delay }}
        />
      ))}

      <div className="absolute inset-0 bg-[radial-gradient(110%_90%_at_50%_42%,transparent_35%,rgba(0,0,0,0.9)_100%)]" />
      <div className="absolute inset-x-0 bottom-0 h-[46%] bg-gradient-to-t from-black via-black/45 to-transparent" />
    </div>
  )
}
